import { useState } from "react";
import { format, isSameDay } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Trash2,
  Youtube,
  Instagram,
  Twitter,
  Clock,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const platformIcons = {
  youtube: Youtube,
  instagram: Instagram,
  twitter: Twitter,
  tiktok: () => (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
    </svg>
  ),
};

const platformColors = {
  youtube: "bg-red-500/20 text-red-400 border-red-500/30",
  instagram: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  twitter: "bg-blue-400/20 text-blue-400 border-blue-400/30",
  tiktok: "bg-white/10 text-white border-white/20",
};

export const SocialScheduler = ({ posts, onCreatePost, onDeletePost }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({
    platform: "youtube",
    content_type: "video",
    title: "",
    scheduled_date: new Date().toISOString(),
  });

  // Get posts for selected date
  const postsForSelectedDate = posts.filter((post) =>
    isSameDay(new Date(post.scheduled_date), selectedDate)
  );

  // Get all scheduled dates for calendar highlighting
  const scheduledDates = posts.map((post) => new Date(post.scheduled_date));

  const handleCreatePost = async () => {
    if (!newPost.title.trim()) {
      toast.error("Please enter a post title");
      return;
    }

    const result = await onCreatePost({
      ...newPost,
      scheduled_date: selectedDate.toISOString(),
    });

    if (result.success) {
      toast.success("Post scheduled successfully");
      setNewPost({
        platform: "youtube",
        content_type: "video",
        title: "",
        scheduled_date: new Date().toISOString(),
      });
      setIsDialogOpen(false);
    } else {
      toast.error("Failed to schedule post");
    }
  };

  const handleDeletePost = async (postId) => {
    const result = await onDeletePost(postId);
    if (result.success) {
      toast.success("Post removed from schedule");
    } else {
      toast.error("Failed to remove post");
    }
  };

  return (
    <div
      className="bg-meta-surface border border-meta-border h-full flex flex-col"
      data-testid="social-scheduler"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Social Scheduler
            </h2>
            <p className="font-sans font-semibold text-meta-text">
              Auto-Post Calendar
            </p>
          </div>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-meta-border text-meta-text hover:border-meta-neon hover:text-meta-neon"
              data-testid="schedule-post-btn"
            >
              <Plus size={14} className="mr-1" />
              Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-meta-surface border-meta-border">
            <DialogHeader>
              <DialogTitle className="text-meta-text">Schedule Post</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <label className="text-meta-text-muted font-mono text-xs uppercase">
                  Platform
                </label>
                <Select
                  value={newPost.platform}
                  onValueChange={(value) =>
                    setNewPost((prev) => ({ ...prev, platform: value }))
                  }
                >
                  <SelectTrigger className="bg-meta-base border-meta-border text-meta-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-meta-elevated border-meta-border">
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-meta-text-muted font-mono text-xs uppercase">
                  Content Type
                </label>
                <Select
                  value={newPost.content_type}
                  onValueChange={(value) =>
                    setNewPost((prev) => ({ ...prev, content_type: value }))
                  }
                >
                  <SelectTrigger className="bg-meta-base border-meta-border text-meta-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-meta-elevated border-meta-border">
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-meta-text-muted font-mono text-xs uppercase">
                  Title
                </label>
                <Input
                  value={newPost.title}
                  onChange={(e) =>
                    setNewPost((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Post title..."
                  className="bg-meta-base border-meta-border text-meta-text font-mono focus:ring-meta-neon"
                  data-testid="post-title-input"
                />
              </div>
              <div className="text-sm text-meta-text-muted">
                Scheduling for:{" "}
                <span className="text-meta-neon font-mono">
                  {format(selectedDate, "MMM d, yyyy")}
                </span>
              </div>
              <Button
                onClick={handleCreatePost}
                className="w-full bg-meta-neon text-meta-base hover:shadow-neon"
                data-testid="confirm-schedule-btn"
              >
                Schedule Post
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar */}
      <div className="p-4 border-b border-meta-border">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          className="mx-auto"
          modifiers={{
            scheduled: scheduledDates,
          }}
          modifiersStyles={{
            scheduled: {
              backgroundColor: "rgba(57, 255, 20, 0.2)",
              borderBottom: "2px solid #39FF14",
            },
          }}
          data-testid="social-calendar"
        />
      </div>

      {/* Selected Date Posts */}
      <div className="flex-1 p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono text-xs uppercase text-meta-text-muted">
            {format(selectedDate, "MMMM d, yyyy")}
          </h3>
          <span className="font-mono text-xs text-meta-neon">
            {postsForSelectedDate.length} scheduled
          </span>
        </div>

        <ScrollArea className="h-[200px]">
          <div className="space-y-2">
            {postsForSelectedDate.map((post) => {
              const PlatformIcon = platformIcons[post.platform] || Clock;
              return (
                <div
                  key={post.id}
                  className="bg-meta-base border border-meta-border p-3 flex items-center justify-between group"
                  data-testid={`scheduled-post-${post.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge
                      className={`${platformColors[post.platform]} border font-mono text-xs shrink-0`}
                    >
                      <PlatformIcon className="w-3 h-3 mr-1" />
                      {post.platform}
                    </Badge>
                    <span className="text-meta-text text-sm truncate">
                      {post.title}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="p-1 text-meta-text-muted hover:text-meta-danger opacity-0 group-hover:opacity-100 transition-all"
                    data-testid={`delete-post-${post.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}

            {postsForSelectedDate.length === 0 && (
              <div className="text-center py-8">
                <Clock className="mx-auto mb-2 text-meta-text-muted" size={24} />
                <p className="text-meta-text-muted text-sm">
                  No posts scheduled
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
